/**
 * Fee Schedule Settings Component
 *
 * Lets practice admins maintain the global claim-charge schedule separately
 * from payer-specific expected allowed amounts.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { CPT_CODES, type CPTCodeOption } from '@components/ui/forms/CPTCodePicker';
import {
  useDeleteBaselineFeeScheduleMutation,
  useDeletePayerExpectedRateMutation,
  useGetBaselineFeeScheduleQuery,
  useGetPayerExpectedRatesQuery,
  useUpdateBaselineFeeScheduleMutation,
  useUpdatePayerExpectedRatesMutation,
} from '@features/billing/store/api';
import { PayerFilter, usePayerStatsQuery } from '@features/billing/components/v2/shared';
import { AttachMoney, Delete, InfoOutlined, Upload } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { FeeScheduleUploadModal } from './FeeScheduleUploadModal';

const PRIMARY_CPT_CODES = CPT_CODES.filter(code => code.category !== 'Add-on');

type FeeScheduleMode = 'standard' | 'payer';
type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

interface FeeScheduleRow {
  cpt_code: string;
  code_id: string;
  description: string;
  category?: string;
  amount_cents: number | null;
  standard_charge_cents: number | null;
  id?: string;
}

function codeIdFromCpt(cptCode: string): string {
  return cptCode.includes(':') ? cptCode : `CPT:${cptCode}`;
}

function bareCode(codeId: string): string {
  return codeId.replace(/^CPT:|^HCPCS:/, '');
}

function formatDollars(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function displayAmount(cents: number | null): string {
  if (cents === null) return '';
  return (cents / 100).toFixed(2);
}

function toCents(dollars: string): number | null {
  const trimmed = dollars.trim();
  if (!trimmed) return null;
  const parsed = Number.parseFloat(trimmed);
  if (Number.isNaN(parsed)) return null;
  return Math.round(parsed * 100);
}

function MoneyInput({
  valueCents,
  placeholder,
  disabled,
  onValueCentsChange,
}: {
  valueCents: number | null;
  placeholder: string;
  disabled?: boolean;
  onValueCentsChange: (valueCents: number | null) => void;
}) {
  const [text, setText] = useState(displayAmount(valueCents));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setText(displayAmount(valueCents));
    }
  }, [isFocused, valueCents]);

  return (
    <TextField
      value={text}
      onFocus={() => setIsFocused(true)}
      onChange={event => {
        const nextText = event.target.value;
        setText(nextText);
        onValueCentsChange(toCents(nextText));
      }}
      onBlur={() => {
        setIsFocused(false);
        setText(displayAmount(toCents(text)));
      }}
      size="small"
      placeholder={placeholder}
      disabled={disabled}
      sx={{ width: '120px' }}
      slotProps={{
        htmlInput: {
          inputMode: 'decimal',
          min: '0',
        },
      }}
    />
  );
}

function HelpContent({ mode, payerName }: { mode: FeeScheduleMode; payerName: string }) {
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          How these numbers are used
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Standard charges are what JotPsych puts on claim lines. Payer expected rates
          are for analytics, forecasting, write-off estimates, and undercharge warnings.
        </Typography>
      </Box>

      <Divider />

      <Box>
        <Typography variant="caption" color="text.secondary">
          Example
        </Typography>
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">99214 claim charge</Typography>
            <Chip label="$200.00" size="small" color="primary" variant="outlined" />
          </Stack>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">
              {payerName || 'Payer'} expected allowed
            </Typography>
            <Chip label="$145.00" size="small" variant="outlined" />
          </Stack>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">Expected write-off</Typography>
            <Chip label="$55.00" size="small" color="warning" variant="outlined" />
          </Stack>
        </Stack>
      </Box>

      <Alert severity={mode === 'standard' ? 'success' : 'info'}>
        {mode === 'standard'
          ? 'Claims always use this global standard charge schedule for now.'
          : 'These payer rates do not change claim submission. They only change expected collection math.'}
      </Alert>
    </Stack>
  );
}

interface FeeScheduleSectionProps {
  initialMode?: FeeScheduleMode;
  onAutosaveSuccess?: () => void;
}

export function FeeScheduleSection({
  initialMode = 'standard',
  onAutosaveSuccess,
}: FeeScheduleSectionProps = {}) {
  const { enqueueSnackbar } = useSnackbar();
  const [mode, setMode] = useState<FeeScheduleMode>(initialMode);
  const [selectedPayerUuid, setSelectedPayerUuid] = useState('');
  const [selectedPayerName, setSelectedPayerName] = useState('');
  const [rates, setRates] = useState<FeeScheduleRow[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedCode, setSelectedCode] = useState<CPTCodeOption | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  const { data: payerStatsData, isLoading: payersLoading } = usePayerStatsQuery();
  const payers = payerStatsData?.payers ?? [];

  const {
    data: feeScheduleData,
    isLoading: isBaselineLoading,
    refetch: refetchBaseline,
  } = useGetBaselineFeeScheduleQuery();

  const {
    data: payerRateData,
    isLoading: isPayerRatesLoading,
    refetch: refetchPayerRates,
  } = useGetPayerExpectedRatesQuery(
    { payerId: selectedPayerUuid },
    { skip: !selectedPayerUuid },
  );

  const [updateFeeSchedule, { isLoading: isUpdatingBaseline }] =
    useUpdateBaselineFeeScheduleMutation();
  const [deleteFeeSchedule, { isLoading: isDeletingBaseline }] =
    useDeleteBaselineFeeScheduleMutation();
  const [updatePayerExpectedRates, { isLoading: isUpdatingPayer }] =
    useUpdatePayerExpectedRatesMutation();
  const [deletePayerExpectedRate, { isLoading: isDeletingPayer }] =
    useDeletePayerExpectedRateMutation();

  const standardRatesByCode = useMemo(() => {
    return new Map(
      (feeScheduleData?.rates ?? []).map((rate: any) => [
        rate.cpt_code,
        {
          id: rate.id as string | undefined,
          code_id: rate.code_id as string | undefined,
          charge_cents: rate.charge_cents as number | null,
        },
      ]),
    );
  }, [feeScheduleData?.rates]);

  useEffect(() => {
    const existingCodes = new Set<string>();
    const expectedRatesByCode = new Map(
      (payerRateData?.rates ?? []).map(rate => [
        rate.cpt_code,
        {
          id: rate.id,
          code_id: rate.code_id,
          expected_allowed_cents: rate.expected_allowed_cents,
        },
      ]),
    );

    const mergedRates: FeeScheduleRow[] = PRIMARY_CPT_CODES.map(cptCode => {
      existingCodes.add(cptCode.cpt);
      const standard = standardRatesByCode.get(cptCode.cpt);
      const expected = expectedRatesByCode.get(cptCode.cpt);
      return {
        cpt_code: cptCode.cpt,
        code_id: standard?.code_id ?? expected?.code_id ?? codeIdFromCpt(cptCode.cpt),
        description: cptCode.description,
        category: cptCode.category,
        amount_cents: mode === 'standard'
          ? standard?.charge_cents ?? null
          : expected?.expected_allowed_cents ?? null,
        standard_charge_cents: standard?.charge_cents ?? null,
        id: mode === 'standard' ? standard?.id : expected?.id,
      };
    });

    (feeScheduleData?.rates ?? []).forEach((rate: any) => {
      if (existingCodes.has(rate.cpt_code)) return;
      existingCodes.add(rate.cpt_code);
      const cptInfo = CPT_CODES.find(c => c.cpt === rate.cpt_code);
      const expected = expectedRatesByCode.get(rate.cpt_code);
      mergedRates.push({
        cpt_code: rate.cpt_code,
        code_id: rate.code_id ?? codeIdFromCpt(rate.cpt_code),
        description: cptInfo?.description ?? `CPT ${rate.cpt_code}`,
        category: cptInfo?.category,
        amount_cents: mode === 'standard'
          ? rate.charge_cents ?? null
          : expected?.expected_allowed_cents ?? null,
        standard_charge_cents: rate.charge_cents ?? null,
        id: mode === 'standard' ? rate.id ?? undefined : expected?.id,
      });
    });

    (payerRateData?.rates ?? []).forEach(rate => {
      if (existingCodes.has(rate.cpt_code)) return;
      const cptInfo = CPT_CODES.find(c => c.cpt === rate.cpt_code);
      mergedRates.push({
        cpt_code: rate.cpt_code,
        code_id: rate.code_id,
        description: cptInfo?.description ?? `CPT ${rate.cpt_code}`,
        category: cptInfo?.category,
        amount_cents: mode === 'payer' ? rate.expected_allowed_cents : null,
        standard_charge_cents: standardRatesByCode.get(rate.cpt_code)?.charge_cents ?? null,
        id: mode === 'payer' ? rate.id : undefined,
      });
    });

    setRates(mergedRates);
    setHasChanges(false);
    setSaveStatus('idle');
  }, [feeScheduleData?.rates, mode, payerRateData?.rates, standardRatesByCode]);

  useEffect(() => {
    if (!selectedPayerUuid && payers.length > 0) {
      setSelectedPayerUuid(payers[0].payer_uuid);
      setSelectedPayerName(payers[0].payer_name);
    }
  }, [payers, selectedPayerUuid]);

  const isLoading = isBaselineLoading || Boolean(mode === 'payer' && selectedPayerUuid && isPayerRatesLoading);
  const isSaving = isUpdatingBaseline || isUpdatingPayer;
  const isDeleting = isDeletingBaseline || isDeletingPayer;

  const handleAmountChange = useCallback((cptCode: string, valueCents: number | null) => {
    setRates(prev =>
      prev.map(rate =>
        rate.cpt_code === cptCode ? { ...rate, amount_cents: valueCents } : rate,
      ),
    );
    setHasChanges(true);
    setSaveStatus('pending');
  }, []);

  const handleAddCode = useCallback(
    (code: CPTCodeOption | null) => {
      if (!code) return;
      if (rates.some(rate => rate.cpt_code === code.cpt)) {
        enqueueSnackbar(`CPT ${code.cpt} is already in the list`, { variant: 'info' });
        return;
      }

      setRates(prev => [
        ...prev,
        {
          cpt_code: code.cpt,
          code_id: codeIdFromCpt(code.cpt),
          description: code.description,
          category: code.category,
          amount_cents: null,
          standard_charge_cents: standardRatesByCode.get(code.cpt)?.charge_cents ?? null,
        },
      ]);
      setSelectedCode(null);
      setHasChanges(true);
      setSaveStatus('pending');
    },
    [enqueueSnackbar, rates, standardRatesByCode],
  );

  const handleRemoveCode = useCallback((cptCode: string) => {
    setRates(prev => prev.filter(rate => rate.cpt_code !== cptCode));
    setHasChanges(true);
    setSaveStatus('pending');
  }, []);

  const handleDeleteRate = useCallback(
    async (row: FeeScheduleRow) => {
      try {
        if (mode === 'standard') {
          await deleteFeeSchedule({ codeId: row.code_id }).unwrap();
          enqueueSnackbar(`Standard charge for ${row.cpt_code} deleted`, { variant: 'success' });
          refetchBaseline();
        } else if (selectedPayerUuid) {
          await deletePayerExpectedRate({
            payerId: selectedPayerUuid,
            codeId: row.code_id,
          }).unwrap();
          enqueueSnackbar(`Expected rate for ${row.cpt_code} deleted`, { variant: 'success' });
          refetchPayerRates();
        }
      } catch (error: any) {
        enqueueSnackbar(error?.data?.message || `Failed to delete rate for ${row.cpt_code}`, {
          variant: 'error',
        });
      }
    },
    [
      deleteFeeSchedule,
      deletePayerExpectedRate,
      enqueueSnackbar,
      mode,
      refetchBaseline,
      refetchPayerRates,
      selectedPayerUuid,
    ],
  );

  const handleSave = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    const rowsToSave = rates
      .filter(rate => rate.amount_cents !== null && rate.cpt_code.trim() !== '')
      .map(rate => ({
        code_id: rate.code_id,
        amount_cents: rate.amount_cents!,
      }));

    if (rowsToSave.length === 0) {
      if (!silent) {
        enqueueSnackbar('Please enter at least one rate', { variant: 'warning' });
      }
      setSaveStatus('idle');
      return false;
    }

    setSaveStatus('saving');
    try {
      if (mode === 'standard') {
        const result = await updateFeeSchedule({
          baselineRatesUpsertRequest: {
            rates: rowsToSave.map(rate => ({
              code_id: rate.code_id,
              charge_cents: rate.amount_cents,
            })),
          },
        }).unwrap();

        if (!silent) {
          enqueueSnackbar(
            `Standard charges updated: ${result.created_count} created, ${result.updated_count} updated`,
            { variant: 'success' },
          );
        }
        refetchBaseline();
      } else {
        if (!selectedPayerUuid) {
          if (!silent) {
            enqueueSnackbar('Select a payer before saving expected rates', { variant: 'warning' });
          }
          setSaveStatus('pending');
          return false;
        }
        const result = await updatePayerExpectedRates({
          payerId: selectedPayerUuid,
          payerExpectedRatesUpsertRequest: {
            rates: rowsToSave.map(rate => ({
              code_id: rate.code_id,
              expected_allowed_cents: rate.amount_cents,
            })),
          },
        }).unwrap();

        if (!silent) {
          enqueueSnackbar(
            `Expected rates updated: ${result.created_count} created, ${result.updated_count} updated`,
            { variant: 'success' },
          );
        }
        refetchPayerRates();
      }
      setHasChanges(false);
      setSaveStatus('saved');
      onAutosaveSuccess?.();
      return true;
    } catch (error: any) {
      setSaveStatus('error');
      enqueueSnackbar(error?.data?.message || 'Failed to update fee schedule', {
        variant: 'error',
      });
      return false;
    }
  }, [
    enqueueSnackbar,
    mode,
    rates,
    refetchBaseline,
    refetchPayerRates,
    selectedPayerUuid,
    onAutosaveSuccess,
    updateFeeSchedule,
    updatePayerExpectedRates,
  ]);

  useEffect(() => {
    if (!hasChanges || isLoading || isSaving) return;
    if (mode === 'payer' && !selectedPayerUuid) return;

    const timeout = window.setTimeout(() => {
      void handleSave({ silent: true });
    }, 900);

    return () => window.clearTimeout(timeout);
  }, [handleSave, hasChanges, isLoading, isSaving, mode, selectedPayerUuid]);

  if (isBaselineLoading) {
    return (
      <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography>Loading fee schedules...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AttachMoney color="primary" />
          <Typography variant="h6" component="h2" sx={{ flex: 1 }}>
            Fee schedules
          </Typography>
          <Button
            size="small"
            variant="text"
            startIcon={<InfoOutlined />}
            onClick={() => setHelpOpen(true)}
          >
            How this works
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Maintain global claim charges and payer expected allowed amounts as separate billing inputs.
        </Typography>

        <Tabs
          value={mode}
          onChange={(_event, value: FeeScheduleMode) => setMode(value)}
          sx={{ mb: 3 }}
        >
          <Tab value="standard" label="Standard claim charges" />
          <Tab value="payer" label="Payer expected rates" />
        </Tabs>

        <Box>
            {mode === 'standard' ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                These are the global amounts submitted on claims for every payer.
              </Alert>
            ) : (
              <Stack spacing={2} sx={{ mb: 2 }}>
                <Box sx={{ maxWidth: 360 }}>
                  <PayerFilter
                    payers={payers}
                    isLoading={payersLoading}
                    value={selectedPayerUuid}
                    onChange={(payerUuid, payerName) => {
                      setSelectedPayerUuid(payerUuid);
                      setSelectedPayerName(payerName);
                    }}
                  />
                </Box>
                <Alert severity="info">
                  Payer expected rates feed collection expectations and analytics. Claims still use the standard charge.
                </Alert>
              </Stack>
            )}

            {isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
                <CircularProgress size={24} />
                <Typography>Loading rates...</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>CPT Code</TableCell>
                      <TableCell>Description</TableCell>
                      {mode === 'payer' && <TableCell align="right">Claim charge</TableCell>}
                      <TableCell align="right">
                        {mode === 'standard' ? 'Claim charge ($)' : 'Expected allowed ($)'}
                      </TableCell>
                      {mode === 'payer' && <TableCell align="right">Expected write-off</TableCell>}
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rates.map((rate, index) => {
                      const writeOff = rate.standard_charge_cents != null && rate.amount_cents != null
                        ? Math.max(rate.standard_charge_cents - rate.amount_cents, 0)
                        : null;
                      const underchargeRisk = mode === 'payer'
                        && rate.standard_charge_cents != null
                        && rate.amount_cents != null
                        && rate.standard_charge_cents < rate.amount_cents;
                      const isCustomUnsaved = !PRIMARY_CPT_CODES.some(c => c.cpt === rate.cpt_code) && !rate.id;

                      return (
                        <TableRow key={rate.cpt_code || `new-${index}`}>
                          <TableCell>
                            <Typography sx={{ fontFamily: 'monospace' }}>{bareCode(rate.code_id)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2">{rate.description}</Typography>
                              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                {rate.category && (
                                  <Typography variant="caption" color="text.secondary">
                                    {rate.category}
                                  </Typography>
                                )}
                                {underchargeRisk && (
                                  <Tooltip title="Standard claim charge is below this expected allowed amount. The payer may cap reimbursement at the billed charge.">
                                    <Chip label="Undercharge risk" color="warning" size="small" variant="outlined" />
                                  </Tooltip>
                                )}
                              </Stack>
                            </Box>
                          </TableCell>
                          {mode === 'payer' && (
                            <TableCell align="right">
                              <Typography variant="body2">
                                {formatDollars(rate.standard_charge_cents)}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell align="right">
                            <MoneyInput
                              valueCents={rate.amount_cents}
                              onValueCentsChange={valueCents => handleAmountChange(rate.cpt_code, valueCents)}
                              placeholder={mode === 'standard' ? '200.00' : '145.00'}
                              disabled={mode === 'payer' && !selectedPayerUuid}
                            />
                          </TableCell>
                          {mode === 'payer' && (
                            <TableCell align="right">
                              <Typography variant="body2">
                                {formatDollars(writeOff)}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell align="right">
                            {rate.id ? (
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteRate(rate)}
                                disabled={isDeleting}
                                color="error"
                                title="Delete saved rate"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            ) : isCustomUnsaved ? (
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveCode(rate.cpt_code)}
                                color="error"
                                title="Remove from list"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <Autocomplete
                value={selectedCode}
                onChange={(_event, newValue) => handleAddCode(newValue)}
                options={CPT_CODES}
                groupBy={option => option.category}
                getOptionLabel={option => `${option.cpt} - ${option.description}`}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {option.cpt}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Add CPT Code"
                    placeholder="Search CPT codes..."
                    size="small"
                  />
                )}
                sx={{ width: 400, maxWidth: '100%' }}
                slotProps={{ listbox: { sx: { maxHeight: 300 } } }}
              />

              <Box sx={{ flex: 1 }} />

              <Button
                onClick={() => setUploadModalOpen(true)}
                variant="outlined"
                startIcon={<Upload />}
                disabled={mode === 'payer' && !selectedPayerUuid}
              >
                {mode === 'standard' ? 'Upload standard charges' : 'Upload payer rates'}
              </Button>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 36 }}>
                {(saveStatus === 'pending' || saveStatus === 'saving' || isSaving) && (
                  <CircularProgress size={16} />
                )}
                <Typography
                  variant="body2"
                  color={saveStatus === 'error' ? 'error' : 'text.secondary'}
                >
                  {saveStatus === 'pending'
                    ? 'Autosaving...'
                    : saveStatus === 'saving' || isSaving
                      ? 'Saving...'
                      : saveStatus === 'saved'
                        ? 'Saved'
                        : saveStatus === 'error'
                          ? 'Autosave failed'
                          : 'Autosaves as you edit'}
                </Typography>
              </Box>
            </Box>
        </Box>

        {isSaving && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Updating fee schedule...
            </Typography>
          </Box>
        )}
      </CardContent>

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Fee schedule usage</DialogTitle>
        <DialogContent>
          <HelpContent mode={mode} payerName={selectedPayerName} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <FeeScheduleUploadModal
        open={uploadModalOpen}
        ratePurpose={mode === 'standard' ? 'standard_charge' : 'payer_expected_rate'}
        payerId={selectedPayerUuid || undefined}
        payerName={selectedPayerName || undefined}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={() => {
          setUploadModalOpen(false);
          refetchBaseline();
          if (selectedPayerUuid) {
            refetchPayerRates();
          }
          enqueueSnackbar(
            mode === 'standard' ? 'Standard charges imported successfully' : 'Payer expected rates imported successfully',
            { variant: 'success' },
          );
        }}
      />
    </Card>
  );
}
